import { useMutation, useQuery } from '@apollo/client';
import { fetchLatLongFromCityPostalCode } from '@components/InputCity/hereApi';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { ADD_JOB_OFFER_TEMPLATE, UPDATE_JOB_OFFER_TEMPLATE, UPDATE_JOB_OFFER_TEMPLATE_UPERCUT, GET_JOB_OFFER_TEMPLATE, GET_JOB_OFFER_TEMPLATES } from '@gql/jobOfferTemplates';
import { GET_PRO } from '@gql/pros';
import { useAuth } from '@contexts/AuthContext';
import { isNull, isUndefined } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

interface UseSaveJobOfferTemplateOptions {
    proId: string;
    template?: any;
    onSuccess?: (values: any) => void;
    onError?: (error: any) => void;
}

const buildTemplatePayload = (data: any, defaultValues: any) => ({
    _id: defaultValues?._id ?? undefined,
    title: data?.title,
    loc: undefined as any,
    brandId: data?.brandId,
    showPhotos: data?.showPhotos,
    showVideo: data?.showVideo,
    city: data?.city,
    postalCode: data?.postalCode,
    address: data?.address,
    assigneeIds: data?.assignees?.map((item: any) => item._id),
    position: {
        companyName: data?.position?.companyName,
        professions: data?.position?.professions,
        numberOfPositions: data?.position?.numberOfPositions
            ? parseInt(data?.position?.numberOfPositions)
            : null,
        place: data?.position?.place,
        allowFreshGrad: data?.position?.allowFreshGrad,
        experience: data?.position?.experience,
        hasTeam: data?.position?.hasTeam ?? false,
        teamNumber: parseInt(data?.position?.teamNumber),
    },
    contract: {
        status: data?.contract?.status,
        contractType: data?.contract?.contractType,
        startDate: data?.contract?.startDate,
        endDate: data?.contract?.endDate,
        rateType: data?.contract?.rateType,
        isAnnualSalary: data?.contract?.isAnnualSalary,
        hourPerWeek: data?.contract?.rateType === 'hourly' ? parseInt(data?.contract?.hourPerWeek) : null,
        dayPerWeek: data?.contract?.dayPerWeek
            ? data?.contract?.dayPerWeek?.map((item: any) => parseFloat(item))?.sort()
            : [5],
        monthPerYear: data?.contract?.monthPerYear ? parseInt(data?.contract?.monthPerYear) : null,
        benefits: data?.contract?.benefits,
        dayOff: {
            number: data?.contract?.dayOff?.number ? parseInt(data?.contract?.dayOff?.number) : null,
            unit: data?.contract?.dayOff?.unit,
        },
        rtt: {
            isActive: data?.contract?.rtt?.isActive ?? false,
            days: parseInt(data?.contract?.rtt?.days),
        },
        guaranteedSalary: {
            min: data?.contract?.guaranteedSalary?.min
                ? parseInt(data?.contract?.guaranteedSalary?.min)
                : null,
            max: data?.contract?.guaranteedSalary?.max
                ? parseInt(data?.contract?.guaranteedSalary?.max)
                : null,
            type: data?.contract?.guaranteedSalary?.type ?? 'Brut',
        },
        commission: data?.contract?.commission?.map((item: any) => ({
            amount: item?.amount ? parseInt(item?.amount) : null,
            origin: item?.origin,
        })),
        bonus: [
            {
                type: data?.contract?.bonus ?? null,
                max: Number(data?.contract?.commissions) ?? null,
            },
        ],
        showSalary: data?.contract?.showSalary,
        remuneration: {
            min: data?.contract?.remuneration?.min ? parseInt(data?.contract?.remuneration?.min) : null,
            max: data?.contract?.remuneration?.max ? parseInt(data?.contract?.remuneration?.max) : null,
        },
        showBonus: data?.contract?.showBonus,
    },
    candidate: {
        softSkills: data?.candidate?.softSkills,
        softSkillIds: data?.candidate?.softSkillIds,
        specialties: data?.candidate?.specialties,
        degree: data?.candidate?.degree ?? null,
        languages:
            data?.candidate?.languages
                ?.filter((item: any) => !!item.languageId && !!item.levelId)
                .map((item: any) => ({
                    languageId: item.languageId,
                    levelId: item.levelId,
                    mandatory: item.mandatory,
                })) ?? [],
    },
    description: {
        missions: data?.description?.missions,
        advantages: data?.description?.advantages,
        listAdvantages: data?.description?.listAdvantages ?? [],
    },
    recruitment: {
        place: data?.recruitment?.place ?? false,
        quickResponse: data?.recruitment?.quickResponse ?? false,
        mode: data?.recruitment?.mode ?? false,
        attempt: data?.recruitment?.attempt ?? false,
        method: data?.recruitment?.method ?? 'direct_email',
        premiumFeatures: data?.recruitment?.premiumFeatures ?? [],
        steps: data?.recruitment?.steps ?? [],
        interviewDate: data?.recruitment?.interviewDate?.map((item: any) => ({
            date: item?.date,
            time: {
                start: item?.time?.start,
                end: item?.time?.end,
            },
        })),
        duration: data?.recruitment?.duration,
    },
    assignees: data?.assignees?.map((item: any) => ({
        _id: item?._id,
        assigneeType: item?.assigneeType,
        role: item?.role,
    })),
});

export const useSaveJobOfferTemplate = ({ proId, template, onSuccess, onError }: UseSaveJobOfferTemplateOptions = {
    proId: null,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { isUpercut } = useAuth();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [data, setData] = useState<any>({});

    const isUpdate = !!template?._id;

    const { data: proData } = useQuery(GET_PRO, {
        variables: { proId },
        skip: !proId,
    });

    const pro = proData?.getPro;

    const defaultValues = useMemo(() => ({
        _id: template?._id ?? undefined,
        assignees: template?.assignees ?? [],
        title: template?.title,
        brandId: template?.brandId,
        showPhotos: template?.showPhotos,
        showVideo: template?.showVideo,
        city: template?.city,
        address: template?.address,
        postalCode: template?.postalCode,
        position: {
            companyName: template?.position?.companyName,
            professions: template?.position?.professions,
            numberOfPositions: template?.position?.numberOfPositions ?? 1,
            place: template?.position?.place,
            allowFreshGrad:
                isNull(template?.position?.allowFreshGrad) || isUndefined(template?.position?.allowFreshGrad)
                    ? true
                    : template?.position?.allowFreshGrad,
            experience: template?.position?.experience,
            hasTeam: template?.position?.hasTeam ?? false,
            teamNumber: template?.position?.teamNumber ? parseInt(template?.position?.teamNumber) : null,
        },
        contract: {
            status: template?.contract?.status,
            contractType: template?.contract?.contractType,
            startDate: template?.contract?.startDate,
            endDate: template?.contract?.endDate,
            rateType: template?.contract?.rateType ?? 'hourly',
            isAnnualSalary: template?.contract?.isAnnualSalary ?? false,
            hourPerWeek: template?.contract?.hourPerWeek ? parseInt(template?.contract?.hourPerWeek) : 35,
            dayPerWeek: template?.contract?.dayPerWeek
                ? template?.contract?.dayPerWeek?.map((item: any) => parseFloat(item))
                : [5],
            monthPerYear: template?.contract?.monthPerYear ? parseInt(template?.contract?.monthPerYear) : 12,
            benefits: template?.contract?.benefits,
            dayOff: {
                number: template?.contract?.dayOff?.number ? parseInt(template?.contract?.dayOff?.number) : 25,
                unit: template?.contract?.dayOff?.unit ?? 'Jours',
            },
            rtt: {
                isActive: template?.contract?.rtt?.isActive ?? false,
                days: template?.contract?.rtt?.days ? parseInt(template?.contract?.rtt?.days) : null,
            },
            guaranteedSalary: {
                min: template?.contract?.guaranteedSalary?.min
                    ? parseInt(template?.contract?.guaranteedSalary?.min)
                    : null,
                max: template?.contract?.guaranteedSalary?.max
                    ? parseInt(template?.contract?.guaranteedSalary?.max)
                    : null,
                type: template?.contract?.guaranteedSalary?.type ?? 'Brut',
            },
            commission: template?.contract?.commission?.map((item: any) => ({
                amount: item?.amount ? parseInt(item?.amount) : null,
                origin: item?.origin,
            })),
            bonus: template?.contract?.bonus?.map((item: any) => item?.type ?? null)?.[0] ?? undefined,
            showSalary:
                isUndefined(template?.contract?.showSalary) || isNull(template?.contract?.showSalary)
                    ? true
                    : template?.contract?.showSalary,
            remuneration: {
                min: template?.contract?.remuneration?.min ? parseInt(template?.contract?.remuneration?.min) : null,
                max: template?.contract?.remuneration?.max ? parseInt(template?.contract?.remuneration?.max) : null,
            },
            showBonus: template?.contract?.showBonus,
        },
        candidate: {
            softSkills: template?.candidate?.softSkills,
            softSkillIds: template?.candidate?.softSkillIds,
            specialties: template?.candidate?.specialties,
            degree: template?.candidate?.degree ?? null,
            languages: template?.candidate?.languages,
        },
        description: {
            missions: template?.description?.missions,
            advantages: template?.description?.advantages,
            listAdvantages: template?.description?.listAdvantages ?? [],
        },
        recruitment: {
            place: template?.recruitment?.place ?? false,
            quickResponse: template?.recruitment?.quickResponse ?? false,
            mode: template?.recruitment?.mode ?? false,
            attempt: template?.recruitment?.attempt ?? false,
            method: template?.recruitment?.method ?? 'direct_email',
            premiumFeatures: template?.recruitment?.premiumFeatures ?? [],
            steps: template?.recruitment?.steps ?? [],
            interviewDate: template?.recruitment?.interviewDate?.map((item: any) => ({
                date: item?.date,
                time: { start: item?.time?.start, end: item?.time?.end },
            })),
            duration: template?.recruitment?.duration,
        },
    }), [template, pro]);

    const methods = useForm({ defaultValues });

    const [save] = useMutation(ADD_JOB_OFFER_TEMPLATE, {
        refetchQueries: [GET_JOB_OFFER_TEMPLATES],
        onCompleted: result => {
            const savedTemplate = result?.addJobOfferTemplatePro;
            enqueueSnackbar('Modèle ajouté avec succès', { variant: 'success' });
            setData(savedTemplate);
            setSuccess(true);
            setLoading(false);
            onSuccess?.(savedTemplate);
        },
        onError: error => {
            enqueueSnackbar(error.message, { variant: 'error' });
            setSuccess(false);
            setLoading(false);
            onError?.(error);
        },
    });

    const [update] = useMutation(isUpercut ? UPDATE_JOB_OFFER_TEMPLATE_UPERCUT : UPDATE_JOB_OFFER_TEMPLATE, {
        refetchQueries: [GET_JOB_OFFER_TEMPLATE, GET_JOB_OFFER_TEMPLATES],
        onCompleted: result => {
            const updatedTemplate = result?.updateJobOfferTemplateUpercut ?? result?.updateJobOfferTemplatePro;
            enqueueSnackbar('Modèle modifié avec succès', { variant: 'success' });
            setData(updatedTemplate);
            setSuccess(true);
            setLoading(false);
            onSuccess?.(updatedTemplate);
        },
        onError: error => {
            enqueueSnackbar(error.message, { variant: 'error' });
            setSuccess(false);
            setLoading(false);
            onError?.(error);
        },
    });

    const submit = useCallback(
        async (formData: any) => {
            setLoading(true);
            const mutate = isUpdate ? update : save;

            const templateInput = buildTemplatePayload(formData, defaultValues);

            const cityChanged = formData?.city !== defaultValues?.city;
            const postalChanged = formData?.postalCode !== defaultValues?.postalCode;

            if (cityChanged || postalChanged) {
                const result = await fetchLatLongFromCityPostalCode(formData?.city, formData?.postalCode);
                templateInput.loc = {
                    type: 'Point',
                    coordinates: [result?.items?.[0]?.position?.lng, result?.items?.[0]?.position?.lat],
                };
            } else {
                templateInput.loc = template?.loc;
            }

            await mutate({ variables: { templateInput } });
        },
        [isUpdate, update, save, defaultValues, template],
    );

    return {
        submit,
        loading,
        success,
        setSuccess,
        data,
        isUpdate,
        methods,
        pro,
    };
};
